import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  Image,
  TouchableOpacity,
} from "react-native";
import { OutlinedTextField } from "rn-material-ui-textfield";
import { useSelector, useDispatch } from "react-redux";
import ArrowRightMedium from "@assets/svg/ArrowRightMedium";
import { setItem } from "@lib/storage";
import Text from "@components/CustomText";
import color from "@constants/color";
import colors from "@constants/color";
import { validationJSON } from "@screens/Auth/TLAddress";
import { getAddressByLatLng, signUp } from "@services/api";
import { fontFamily } from "@utils/styles";
import analytics from "@services/analytics";
import { EARLY_JOURNEY } from "@constants/analyticsEventsCT";

import { actions as authActions } from "../../../reducers/authReducer";
import { scale } from "@utils/scale";
import { numberValidator, lengthValidator } from "@utils/validators";

const EnterAddress = ({ navigation, route }) => {
  const [add, setAdd] = useState({});
  const user = useSelector(({ auth }) => auth.user);
  const [address1, setAddress1] = useState(
    route.params?.address1 ?? user.address1 ?? "",
  );
  const [address2, setAddress2] = useState(
    route.params?.address2 ?? user.address2 ?? "",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});

  const region = {
    latitude: parseFloat(route.params?.lat || (user && user.lat)),
    longitude: parseFloat(route.params?.lng || (user && user.lng)),
    latitudeDelta: 0.015,
    longitudeDelta: 0.0121,
  };

  useEffect(() => {
    addressFromLatLng(region?.latitude, region?.longitude);
    analytics.trackEvent(EARLY_JOURNEY.ADDRESS_FORM_LANDING, {
      flow: "signup flow",
    });
  }, []);

  const addressFromLatLng = (lat, lng) => {
    getAddressByLatLng(lat, lng)
      .then((res) => {
        if (res.data.results.length > 0) {
          setAdd({
            address: res.data.results[0].formatted_address,
            pincode: res.data.results[0].pincode,
            city: res.data.results[0].heading,
          });
          if (res.data.results[0].pincode !== user?.pincode) {
            setAddress1("");
            setAddress2("");
          }
        }
      })
      .catch((err) => {
        console.log("error", err);
      });
  };

  const submitAddress = async () => {
    if (isSubmitting) return;
    const values = formValidator(validationJSON, { address1, address2 });
    const payload = {
      address1: address1
        .replace(/\s+/g, " ")
        .replace(/^\s/, "")
        .replace(/\s$/, "")
        .replace(/^,|,$/g, ""),
      address2: address2
        .replace(/\s+/g, " ")
        .replace(/^\s/, "")
        .replace(/\s$/, "")
        .replace(/^,|,$/g, ""),
      pincode: add.pincode,
    };
    try {
      if (values.isValid) {
        setIsSubmitting(true);
        await signUp(payload);
        await setItem("address_added", "true");
        analytics.trackEvent(EARLY_JOURNEY.ADDRESS_FORM_SUBMITTED, {
          flow: "signup flow",
        });
        dispatch(authActions.updateShowAddressScreen({ address1, address2 }));
      } else {
        setErrors(values.errorObj);
        setIsSubmitting(false);
      }
    } catch (e) {
      console.log("error", e);
      setIsSubmitting(false);
    }
  };

  const _onChangeText = (setterMethod, key) => (text) => {
    setterMethod(text?.trimStart());
    if (key === "address1") {
      if (checkSpecialCharacters(text)) {
        setErrors({
          ...errors,
          address1: "Invalid input",
        });
      } else {
        setErrors({
          ...errors,
          address1: "",
        });
      }
    } else if (key === "address2") {
      if (checkSpecialCharacters(text)) {
        setErrors({
          ...errors,
          address2: "Invalid input",
        });
      } else {
        setErrors({
          ...errors,
          address2: "",
        });
      }
    }
  };

  const onBlur = (index) => {
    if (index === 0) {
      if (checkSpecialCharacters(address1)) {
        setErrors({
          ...errors,
          address1: "Invalid input",
        });
      } else {
        setErrors({
          ...errors,
          address1: "",
        });
      }
    } else if (index === 1) {
      if (checkSpecialCharacters(address2)) {
        setErrors({
          ...errors,
          address2: "Invalid input",
        });
      } else {
        setErrors({
          ...errors,
          address2: "",
        });
      }
    }
  };

  const onPressLocationChange = () => {
    analytics.trackEvent(EARLY_JOURNEY.ADDRESS_CHANGE_CTA_CLICKED, {
      flow: "signup flow",
    });
    navigation.replace("LocationViaAddress", {
      fromEnterAddress: true,
    });
  };

  const disabled =
    !address1 ||
    !address2 ||
    address1.trim().length < 2 ||
    address2.trim().length < 2;

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <Text newfont variant="title_18">
          Apna address dalein
        </Text>
      </View>
      <ScrollView stickyHeaderIndices={1} style={styles.scrollview}>
        <View style={styles.sview1}>
          <View style={styles.row}>
            <Text
              newfont
              variant="body_text_2"
              style={{ color: color.lightBlack }}
            >
              Aapki Location
            </Text>
            <Text
              newfont
              variant="body_text_2"
              onPress={onPressLocationChange}
              style={styles.changelocationtext}
            >
              {"Change"}
            </Text>
          </View>
          <View style={styles.locationContentContainer}>
            <Image
              resizeMode="contain"
              style={styles.locationImage}
              source={require("@assets/images/you_are_here.webp")}
            />
            <View>
              <Text newfont variant="title_18" style={{ color: "#000" }}>
                {add?.city}
              </Text>
              <Text
                newfont
                variant="body_text_2"
                style={{ color: color.lightBlack, maxWidth: scale(280) }}
              >
                {add?.address}
              </Text>
            </View>
          </View>

          <View style={styles.inputContainer}>
            <OutlinedTextField
              label={"House / Flat / Floor No.*"}
              error={errors.address1}
              title={""}
              keyboardType={"default"}
              onChangeText={_onChangeText(setAddress1, "address1")}
              labelFontSize={14}
              tintColor="rgba(0, 0, 0, .38)"
              disabledLineWidth={0}
              activeLineWidth={0.75}
              maxLength={50}
              value={address1}
              editable={true}
              labelTextStyle={styles.labelTextStyle}
              style={styles.textInput}
              autofocus={0}
              onBlur={() => onBlur(0)}
            />
            <OutlinedTextField
              label={"Apartment / Road / Area*"}
              error={errors.address2}
              title={""}
              keyboardType={"default"}
              onChangeText={_onChangeText(setAddress2, "address2")}
              labelFontSize={14}
              tintColor="rgba(0, 0, 0, .38)"
              disabledLineWidth={0}
              activeLineWidth={0.75}
              maxLength={50}
              value={address2}
              editable={true}
              labelTextStyle={styles.labelTextStyle}
              style={styles.textInput}
              autofocus={0}
              onBlur={() => onBlur(1)}
            />
          </View>
        </View>
      </ScrollView>
      <View style={styles.footerContainer}>
        <TouchableOpacity
          disabled={disabled}
          onPress={submitAddress}
          style={[
            styles.cta,
            { backgroundColor: disabled ? "rgba(0, 0, 0, 0.1)" : "#E40046" },
          ]}
        >
          <Text newfont variant="title_18" style={styles.ctaText}>
            Dukaan ka address add karen
          </Text>
          <ArrowRightMedium width={14} height={11} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default EnterAddress;

const checkSpecialCharacters = (val, key) => {
  if (key === "landmark" && !val) {
    return false;
  }
  if (!val) {
    return;
  }
  let format = /[`•√π÷×¶∆€¥¢°✓™®©!£@#$%^&*()_+\=\[\]{};':"|<>?~]/;
  if (key === "pincode") {
    format = /[` •√π÷×¶∆€¥¢°✓™®©!£@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
  }
  if (key === "city") format = { test: (city) => !(city?.length > 2) };

  const hasSpecial = format.test(val.trim());
  return hasSpecial;
};

const checkForOnlyNumbers = (val, key) => {
  const format = /^\d+$/;
  if (key === "pincode") {
    return false;
  }
  if (!val) {
    return false;
  }
  const hasOnlyNumber = format.test(val.trim());
  return hasOnlyNumber;
};

const formValidator = (validationJson = {}, data = {}) => {
  let isValid = true;
  const errorObj = {};
  const { address1 = "", address2 = "" } = data;

  const valueObj = {
    address1,
    address2,
  };
  Object.keys(valueObj).forEach((v) => {
    if (validationJson[v]) {
      if (checkSpecialCharacters(valueObj[v], v)) {
        isValid = isValid ? false : isValid;
        errorObj[v] = "Invalid input";
      }
      if (checkForOnlyNumbers(valueObj[v], v)) {
        isValid = isValid ? false : isValid;
        errorObj[v] = "Invalid input";
      }
      if (validationJson[v].isRequired) {
        if (!valueObj[v] || (valueObj[v] && valueObj[v].trim() === "")) {
          isValid = isValid ? false : isValid;
          errorObj[v] = "Invalid input";
        }
      }
      if (validationJson[v].isNumber) {
        if (
          !numberValidator(
            valueObj[v],
            validationJson[v].min,
            validationJson[v].max,
          )
        ) {
          isValid = isValid ? false : isValid;
          errorObj[v] = "Invalid input";
        }
      } else {
        if (validationJson[v].min && validationJson[v].max) {
          if (
            !lengthValidator(
              valueObj[v],
              validationJson[v].min,
              validationJson[v].max,
            )
          ) {
            isValid = isValid ? false : isValid;
            errorObj[v] = "Address vistaar me bataiye";
          }
        }
      }
    }
  });
  return { isValid, errorObj, allValues: valueObj };
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    // overflow: "hidden",
  },
  headerContainer: {
    height: 56,
    paddingHorizontal: 16,
    justifyContent: "center",
    elevation: 2,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4.65,
    elevation: 8,
    shadowColor: color.mediumBlack,
    backgroundColor: color.white,
  },
  scrollview: {
    backgroundColor: "white",
  },
  sview1: {
    marginTop: 12,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginHorizontal: 15,
  },
  changelocationtext: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    justifyContent: "center",
    alignItems: "center",
    color: "#E40046",
    fontSize: 12,
    fontWeight: "bold",
    borderColor: "rgba(0, 0, 0, 0.1)",
    borderWidth: 1,
    borderRadius: 50,
    backgroundColor: "rgba(0, 0, 0, 0.02)",
  },
  locationContentContainer: {
    marginTop: 15,
    flexDirection: "row",
    // backgroundColor: "red",
    paddingHorizontal: 15,
    paddingBottom: 12,
    borderBottomColor: "rgba(0, 0, 0, 0.1)",
    borderBottomWidth: 1,
  },
  locationImage: {
    height: 24,
    width: 24,
    marginTop: 5,
    marginRight: 10,
  },
  inputContainer: {
    padding: 15,
    borderRadius: 6,
  },
  textInput: {
    marginBottom: -3,
    fontFamily: fontFamily.semiBold,
  },
  footerContainer: {
    paddingHorizontal: 15,
    borderTopWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.1)",
    elevation: 5,
    backgroundColor: "#fff",
  },
  cta: {
    padding: 10,
    backgroundColor: colors.primary,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 10,
    marginBottom: 10,
    borderRadius: 8,
  },
  ctaText: {
    color: "#fff",
    marginRight: 10,
  },
  labelTextStyle: {
    paddingTop: 6,
    fontSize: 30,
    fontFamily: fontFamily.regular,
  },
});
